import { fetchAuthSession } from 'aws-amplify/auth';
import { getRestApiBaseUrl } from '../config/websocket';

/** Normalized property context shape returned by backend tools (saved with project sites). */
export type SelaGeoJsonFeatureCollection = {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties?: Record<string, any>;
    geometry: {
      type: string;
      coordinates: any;
    };
  }>;
};

export type SelaGeoLayer = {
  id: string;
  label: string;
  kind: 'parcel' | 'zoning' | 'eca' | string;
  sourceUrl?: string;
  mapUrl?: string;
  riskLevel?: string;
  featureCollection: SelaGeoJsonFeatureCollection;
  bounds?: [number, number, number, number] | null;
};

export type SeattleDataOut = {
  geocode: { lat: number; lng: number; label: string } | null;
  zoning: any | null;
  parcel: any | null;
  permits: any[];
  overlays: Array<{
    type: string;
    label: string;
    name?: string;
    sourceUrl?: string;
    layerId?: number;
    layerName?: string;
    featureCount?: number;
    riskLevel?: string;
    riskCategory?: string;
    riskSummary?: string;
    recommendedDocuments?: string[];
  }>;
  geoLayers?: SelaGeoLayer[];
  landmarks: any[];
  sources: Array<{ name: string; url: string; status?: string; error?: string }>;
  errors?: string[];
  confidence?: number;
};

export type StageKey = 'siteEval' | 'feasibility' | 'design' | 'entitlement';
export type StageVisual = 'complete' | 'current' | 'upcoming';
export type ActiveStageBadgeVariant = 'blue' | 'amber' | 'green';

export type ProjectLocationMode = 'address' | 'parcel';

export type SelaEvaluationMode = 'parcel_evaluation';

export type SelaDevelopmentPath =
  | 'adu_dadu'
  | 'remodel_addition'
  | 'scrape_rebuild'
  | 'single_family_home'
  | 'small_multifamily_townhomes'
  | 'short_plat_subdivision'
  | 'buy_hold_rental'
  | 'recommend_paths';

export type SelaDealStage =
  | 'early_screening'
  | 'considering_offer'
  | 'under_contract'
  | 'parcel_controlled'
  | 'active_predevelopment';

export type SelaPriority =
  | 'fastest_permit_path'
  | 'max_buildable_units'
  | 'lowest_entitlement_risk'
  | 'lowest_construction_complexity'
  | 'best_resale_upside'
  | 'best_rental_income'
  | 'avoid_environmental_site_surprises';

export type SelaDealContextDates = {
  feasibilityDeadline?: string;
  closingDate?: string;
  targetPermitSubmission?: string;
  targetGroundbreak?: string;
};

export type SelaProjectCard = {
  id: string;
  typeLabel: string;
  activeStageLabel: string;
  activeStageVariant: ActiveStageBadgeVariant;
  title: string;
  address: string;
  stages: Record<StageKey, StageVisual>;
  disciplines: string;
  updated: string;
  locationMode: ProjectLocationMode;
  parcelPin?: string | null;
  city?: string | null;
  state?: string | null;
  cityKey?: string | null;
  evaluationMode?: SelaEvaluationMode | null;
  developmentPaths: SelaDevelopmentPath[];
  dealStage?: SelaDealStage | null;
  priorities: SelaPriority[];
  dealContextDates?: SelaDealContextDates | null;
};

export type SelaProjectSite = {
  siteId: string;
  address: string;
  label: string;
  overviewText: string;
  geocode: SeattleDataOut['geocode'];
  parcel: SeattleDataOut['parcel'];
  zoning: SeattleDataOut['zoning'];
  permits: SeattleDataOut['permits'];
  overlays: SeattleDataOut['overlays'];
  geoLayers: SelaGeoLayer[];
  sources: SeattleDataOut['sources'];
  summary: {
    parcelPin?: string;
    zoningCode?: string;
    permitCount?: number;
  };
  report: {
    reportId?: string;
    status?: string;
    fileName?: string;
    objectKey?: string;
    downloadUrl?: string;
    updatedAt?: string | number;
  };
  updatedAt?: string;
};

export type SelaProjectDetail = SelaProjectCard & {
  primarySiteId?: string;
  createdAt?: string;
  activeStageId?: string;
  primarySite?: SelaProjectSite | null;
};

export type SelaCreateProjectRequest = {
  projectName: string;
  projectType: string;
  locationMode: ProjectLocationMode;
  propertyAddress?: string;
  parcelPin?: string;
  city?: string;
  state?: string;
  evaluationMode?: SelaEvaluationMode;
  developmentPaths?: SelaDevelopmentPath[];
  dealStage?: SelaDealStage;
  priorities?: SelaPriority[];
  dealContextDates?: SelaDealContextDates;
};

export type SaveProjectSiteRequest = {
  propertyAddress: string;
  propertyContext: SeattleDataOut;
  overviewText?: string;
  reportId?: string;
  reportStatus?: string;
  reportDownloadUrl?: string;
  reportFileName?: string;
  analysisSessionId?: string | null;
};

type ApiProject = {
  id: string;
  title: string;
  address: string;
  typeLabel: string;
  activeStageLabel: string;
  activeStageVariant: ActiveStageBadgeVariant;
  stages: Record<StageKey, StageVisual>;
  disciplinesCount: number;
  updatedAt?: string;
  createdAt?: string;
  primarySiteId?: string;
  activeStageId?: string;
  primarySite?: SelaProjectSite | null;
  locationMode?: ProjectLocationMode | string | null;
  parcelPin?: string | null;
  city?: string | null;
  state?: string | null;
  cityKey?: string | null;
  evaluationMode?: SelaEvaluationMode | string | null;
  developmentPaths?: SelaDevelopmentPath[] | string[] | null;
  dealStage?: SelaDealStage | string | null;
  priorities?: SelaPriority[] | string[] | null;
  dealContextDates?: SelaDealContextDates | null;
};

async function getAuthToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString() || 'anon';
  } catch {
    return 'anon';
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthToken();
  const response = await fetch(`${getRestApiBaseUrl()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || `Request failed (${response.status})`);
  }
  return payload as T;
}

function formatRelativeUpdated(updatedAt?: string): string {
  if (!updatedAt) {
    return 'Updated recently';
  }

  const timestamp = new Date(updatedAt).getTime();
  if (Number.isNaN(timestamp)) {
    return 'Updated recently';
  }

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.max(1, Math.floor(diffMs / 60000));
  if (diffMinutes < 60) {
    return `Updated ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `Updated ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `Updated ${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  return `Updated ${new Date(updatedAt).toLocaleDateString()}`;
}

function mapProject(project: ApiProject): SelaProjectDetail {
  const locationMode: ProjectLocationMode =
    project.locationMode === 'parcel' ? 'parcel' : 'address';
  const evaluationMode: SelaEvaluationMode | null =
    project.evaluationMode === 'parcel_evaluation' ? 'parcel_evaluation' : null;
  return {
    id: project.id,
    typeLabel: project.typeLabel,
    activeStageLabel: project.activeStageLabel,
    activeStageVariant: project.activeStageVariant,
    title: project.title,
    address: project.address,
    stages: project.stages,
    disciplines: `${project.disciplinesCount} discipline${project.disciplinesCount === 1 ? '' : 's'}`,
    updated: formatRelativeUpdated(project.updatedAt),
    primarySiteId: project.primarySiteId,
    createdAt: project.createdAt,
    activeStageId: project.activeStageId,
    primarySite: project.primarySite,
    locationMode,
    parcelPin: project.parcelPin ?? null,
    city: project.city ?? null,
    state: project.state ?? null,
    cityKey: project.cityKey ?? null,
    evaluationMode,
    developmentPaths: Array.isArray(project.developmentPaths)
      ? (project.developmentPaths as SelaDevelopmentPath[])
      : [],
    dealStage: (project.dealStage as SelaDealStage | null | undefined) ?? null,
    priorities: Array.isArray(project.priorities)
      ? (project.priorities as SelaPriority[])
      : [],
    dealContextDates: project.dealContextDates ?? null,
  };
}

export type SelaProjectListResult = {
  projects: SelaProjectCard[];
  canCreateProject: boolean;
};

export async function listSelaProjects(): Promise<SelaProjectListResult> {
  const payload = await apiFetch<{
    success: boolean;
    projects: ApiProject[];
    canCreateProject?: boolean;
  }>('/sela/projects', {
    method: 'GET',
  });
  const projects = (payload.projects || []).map(mapProject);
  return {
    projects,
    canCreateProject:
      typeof payload.canCreateProject === 'boolean'
        ? payload.canCreateProject
        : projects.length === 0,
  };
}

export async function deleteSelaProject(
  projectId: string,
): Promise<{ projectId: string; status: string }> {
  const response = await apiFetch<{
    success: boolean;
    projectId: string;
    status: string;
  }>(`/sela/projects/${projectId}`, {
    method: 'DELETE',
  });
  return {
    projectId: response.projectId || projectId,
    status: response.status || 'deleted',
  };
}

export async function createSelaProject(payload: SelaCreateProjectRequest): Promise<SelaProjectDetail> {
  const response = await apiFetch<{ success: boolean; project: ApiProject }>('/sela/projects', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return mapProject(response.project);
}

export async function getSelaProject(projectId: string): Promise<SelaProjectDetail> {
  const response = await apiFetch<{ success: boolean; project: ApiProject }>(`/sela/projects/${projectId}`, {
    method: 'GET',
  });
  return mapProject(response.project);
}

export async function saveProjectPrimarySite(
  projectId: string,
  payload: SaveProjectSiteRequest,
): Promise<SelaProjectDetail> {
  const response = await apiFetch<{ success: boolean; project: ApiProject }>(`/sela/projects/${projectId}/sites`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return mapProject(response.project);
}

export async function runSelaProjectIntel(projectId: string): Promise<SelaProjectDetail> {
  const response = await apiFetch<{ success: boolean; project: ApiProject }>(
    `/sela/projects/${projectId}/intel/run`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
  );
  return mapProject(response.project);
}

export type SelaChangelogEntry = {
  changeId: string;
  projectId: string;
  action: 'created' | 'updated' | 'status_changed' | 'generated' | 'deleted' | 'uploaded' | 'completed' | 'failed' | string;
  entityType: string;
  entityId?: string;
  summary: string;
  actorUserId?: string;
  actorSource?: 'user' | 'system' | 'pipeline' | string;
  changes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  createdAt?: string;
};

export type SelaChangelogPage = {
  changes: SelaChangelogEntry[];
  nextCursor?: string;
};

export async function listSelaProjectChangelog(
  projectId: string,
  options: { limit?: number; cursor?: string } = {},
): Promise<SelaChangelogPage> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await apiFetch<{ success: boolean } & SelaChangelogPage>(
    `/sela/projects/${projectId}/changelog${suffix}`,
    { method: 'GET' },
  );
  return {
    changes: response.changes || [],
    nextCursor: response.nextCursor,
  };
}

export type SelaChatCitation = {
  id: number;
  type?: 'graph_fact' | string;
  source: string;
  source_link?: string | null;
  page?: string;
  chunk?: string;
  text?: string;
  answer?: string;
  query?: string;
  nodeId?: string;
  fact?: {
    label?: string;
    value?: string;
    unit?: string;
    category?: string;
    confidence?: number;
  };
  sources?: SelaSourceRef[];
  citations?: Array<{
    id?: number;
    source?: string;
    source_link?: string | null;
    page?: string;
    chunk?: string;
    text?: string;
  }>;
};

export type SelaDecisionBriefConfidence = 'High' | 'Medium' | 'Low' | 'Unknown';
export type SelaDecisionBriefFeasibility =
  | 'Strong'
  | 'Likely'
  | 'Conditional'
  | 'Risky'
  | 'Low'
  | 'Blocked'
  | 'Unknown / Needs Verification';
export type SelaDecisionBriefSeverity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Unknown';
export type SelaDecisionBriefUrgency = 'Immediate' | 'High' | 'Medium' | 'Low';

export type SelaDecisionBriefContextSummary = {
  subjectParcel: string;
  selectedDevelopmentPaths: string[];
  dealStage: string;
  priorities: string[];
  optionalDates: string[];
};

export type SelaDecisionBriefEvidenceReference = number | string;

export type SelaDecisionBriefOverallRecommendation = {
  label: string;
  rationale: string;
  confidence: SelaDecisionBriefConfidence;
  confidenceRationale: string;
  mostImportantUnresolvedIssue: string;
  suggestedImmediatePosture: string;
  evidenceReferences: SelaDecisionBriefEvidenceReference[];
};

export type SelaDecisionBriefFeasibilityRow = {
  pathName: string;
  feasibilityRating: SelaDecisionBriefFeasibility;
  confidence: SelaDecisionBriefConfidence;
  mainRationale: string;
  keyBlockers: string[];
  nextVerificationStep: string;
  evidenceReferences: SelaDecisionBriefEvidenceReference[];
};

export type SelaDecisionBriefDealKiller = {
  riskTitle: string;
  severity: SelaDecisionBriefSeverity;
  affectedDevelopmentPaths: string[];
  whyItMatters: string;
  whatToVerifyNext: string;
  whoMayNeedToBeInvolved: string[];
  evidenceReferences: SelaDecisionBriefEvidenceReference[];
};

export type SelaDecisionBriefMissingFact = {
  missingFact: string;
  whyItMatters: string;
  howToResolveIt: string;
  affectedDevelopmentPaths: string[];
  relatedRisk: string;
};

export type SelaDecisionBriefNextAction = {
  actionTitle: string;
  reason: string;
  urgency: SelaDecisionBriefUrgency;
  relatedRiskOrMissingFact: string;
  relevantStage: string;
  proposedRoadmapRef?: {
    type: 'gate_deliverable' | string;
    gateId: string;
    deliverableId: string;
    label: string;
  };
  roadmapRef?: {
    type: 'gate_deliverable' | string;
    gateId: string;
    deliverableId: string;
    label: string;
  };
  completionState?: string;
};

export type SelaDecisionBriefEvidenceAndSources = {
  confirmedFacts: string[];
  inferredRisks: string[];
  missingInformation: string[];
  recommendedNextActions: string[];
  sourceBackedEvidence: string[];
  evidenceGaps: string;
};

export type SelaDecisionBrief = {
  artifactType: 'parcel_decision_brief' | string;
  version: string;
  generatedAt?: string;
  contextSummary: SelaDecisionBriefContextSummary;
  overallRecommendation: SelaDecisionBriefOverallRecommendation;
  developmentPathFeasibility: SelaDecisionBriefFeasibilityRow[];
  topDealKillers: SelaDecisionBriefDealKiller[];
  criticalMissingFacts: SelaDecisionBriefMissingFact[];
  nextActions: SelaDecisionBriefNextAction[];
  fullAnalysis: string;
  evidenceAndSources: SelaDecisionBriefEvidenceAndSources;
};

export type SelaChatMessage = {
  messageId: string;
  projectId?: string;
  role: 'user' | 'assistant';
  kind: 'overview' | 'question' | 'refresh';
  content: string;
  status: 'pending' | 'complete' | 'error';
  citations: SelaChatCitation[];
  decisionBrief?: SelaDecisionBrief | null;
  errorMessage?: string;
  agentMetadata?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
};

export async function listProjectInsightsMessages(
  projectId: string,
): Promise<SelaChatMessage[]> {
  const response = await apiFetch<{ success: boolean; messages: SelaChatMessage[] }>(
    `/sela/projects/${projectId}/insights/messages`,
    { method: 'GET' },
  );
  return response.messages || [];
}

export type PostInsightsMessageOptions = {
  /** Active WebSocket connection id so the SQS worker can stream chunks back live. */
  connectionId?: string | null;
  /** WebSocket API Gateway domain (host without protocol). */
  wsDomain?: string | null;
  /** WebSocket API Gateway stage (path segment after the domain). */
  wsStage?: string | null;
  /** Optional bounded UI context for project-wide Ask Sela launches. */
  askContext?: unknown;
};

export async function rerunProjectInsights(
  projectId: string,
  options: PostInsightsMessageOptions = {},
): Promise<SelaChatMessage[]> {
  const response = await apiFetch<{ success: boolean; messages: SelaChatMessage[] }>(
    `/sela/projects/${projectId}/insights/overview/rerun`,
    {
      method: 'POST',
      body: JSON.stringify({
        connection_id: options.connectionId || undefined,
        ws_domain: options.wsDomain || undefined,
        ws_stage: options.wsStage || undefined,
      }),
    },
  );
  return response.messages || [];
}

export async function postProjectInsightsMessage(
  projectId: string,
  question: string,
  options: PostInsightsMessageOptions = {},
): Promise<SelaChatMessage[]> {
  const response = await apiFetch<{ success: boolean; messages: SelaChatMessage[] }>(
    `/sela/projects/${projectId}/insights/messages`,
    {
      method: 'POST',
      body: JSON.stringify({
        question,
        connection_id: options.connectionId || undefined,
        ws_domain: options.wsDomain || undefined,
        ws_stage: options.wsStage || undefined,
        ask_context: options.askContext || undefined,
      }),
    },
  );
  return response.messages || [];
}

// ----------------------------------------------------------------------
// Documents (PDF upload + extraction pipeline)
// ----------------------------------------------------------------------

export type SelaDocumentStatus =
  | 'uploaded'
  | 'textract'
  | 'rendering'
  | 'extracting'
  | 'validating'
  | 'writing'
  | 'extracted'
  | 'failed';

export type SelaDocumentWorkflowContext = 'timeline' | 'permit' | 'blocker' | 'library';

export type SelaDocumentMetadata = {
  workflowContext?: SelaDocumentWorkflowContext;
  stageId?: string;
  gateId?: string;
  deliverableId?: string;
  requirementId?: string;
  blockerId?: string;
  uploadReason?: string;
  uploadedFor?: string;
  requiredBecause?: string;
  uploadedByUserId?: string;
  uploadedByName?: string;
  uploadedByEmail?: string;
  uploadedAt?: string;
  assignedAt?: string;
  assignedByUserId?: string;
};

export type SelaDocument = {
  documentId: string;
  projectId: string;
  fileName: string;
  fileSize?: number;
  contentType?: string;
  s3Bucket?: string;
  s3Key?: string;
  processingStatus: SelaDocumentStatus;
  pageCount?: number;
  errorMessage?: string;
  executionArn?: string;
  createdAt?: string;
  updatedAt?: string;
} & SelaDocumentMetadata;

export type SelaDocumentUpload = {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  bucket: string;
  key: string;
  expiresIn: number;
};

export const TERMINAL_DOCUMENT_STATUSES: ReadonlyArray<SelaDocumentStatus> = [
  'extracted',
  'failed',
];

export function isTerminalDocumentStatus(
  status: SelaDocumentStatus | string | undefined,
): boolean {
  return TERMINAL_DOCUMENT_STATUSES.includes(status as SelaDocumentStatus);
}

export async function listSelaDocuments(projectId: string): Promise<SelaDocument[]> {
  const response = await apiFetch<{ success: boolean; documents: SelaDocument[] }>(
    `/sela/projects/${projectId}/documents`,
    { method: 'GET' },
  );
  return response.documents || [];
}

export async function createSelaDocument(
  projectId: string,
  file: File,
  metadata: SelaDocumentMetadata = {},
): Promise<{ document: SelaDocument; upload: SelaDocumentUpload }> {
  return apiFetch<{ document: SelaDocument; upload: SelaDocumentUpload; success: boolean }>(
    `/sela/projects/${projectId}/documents`,
    {
      method: 'POST',
      body: JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || 'application/pdf',
        ...metadata,
      }),
    },
  );
}

export async function uploadSelaDocumentToS3(
  upload: SelaDocumentUpload,
  file: File,
): Promise<void> {
  const response = await fetch(upload.url, {
    method: upload.method,
    headers: upload.headers,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed (${response.status})`);
  }
}

export async function startSelaDocumentProcessing(
  projectId: string,
  documentId: string,
): Promise<SelaDocument> {
  const response = await apiFetch<{
    success: boolean;
    document: SelaDocument;
    executionArn?: string;
  }>(`/sela/projects/${projectId}/documents/${documentId}/start`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return response.document;
}

// ----------------------------------------------------------------------
// Permit workflow
// ----------------------------------------------------------------------

export type PermitRequirementStatus = 'missing' | 'needs_review' | 'complete' | 'not_applicable';
export type PermitCommentStatus = 'open' | 'in_progress' | 'resolved' | 'deferred';
export type PermitCycleStatus = 'draft' | 'submitted' | 'returned' | 'closed';

export type PermitPacket = {
  projectId?: string;
  packetId: string;
  jurisdiction?: string;
  permitType?: string;
  generatedAt?: string;
  completenessScore: number;
  blockersCount: number;
  requirementCount: number;
  readyStatus: 'ready' | 'not_ready' | string;
  createdAt?: string;
  updatedAt?: string;
};

export type PermitRequirement = {
  projectId?: string;
  itemType?: 'RequiredDocument';
  requirementId: string;
  label: string;
  category: string;
  severity: 'low' | 'medium' | 'high' | string;
  requiredBecause: string;
  status: PermitRequirementStatus;
  inferredStatus?: PermitRequirementStatus;
  manualStatus?: string;
  owner?: string;
  dueDate?: string;
  notes?: string;
  matchedDocumentIds: string[];
  evidenceNodeIds: string[];
  createdAt?: string;
  updatedAt?: string;
};

export type PermitReviewCycle = {
  cycleId: string;
  cycleNumber: number;
  status: PermitCycleStatus;
  submittedAt?: string;
  returnedAt?: string;
  sourceDocumentIds: string[];
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PermitReviewComment = {
  commentId: string;
  cycleId: string;
  reviewerDiscipline: string;
  commentText: string;
  status: PermitCommentStatus;
  owner?: string;
  dueDate?: string;
  responseText?: string;
  linkedRequirementIds: string[];
  sourceDocumentId?: string;
  sourcePage?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PermitResubmittalBundle = {
  bundleId: string;
  includedDocumentIds: string[];
  changedDocumentIds: string[];
  openCommentIds: string[];
  missingRequirementIds: string[];
  readinessStatus: 'ready' | 'not_ready' | string;
  generatedMatrix: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PermitWorkflow = {
  packet: PermitPacket;
  requirements: PermitRequirement[];
  reviewCycles: PermitReviewCycle[];
  comments: PermitReviewComment[];
  resubmittalBundles: PermitResubmittalBundle[];
};

export async function getPermitWorkflow(projectId: string): Promise<PermitWorkflow> {
  return apiFetch<PermitWorkflow & { success: boolean }>(
    `/sela/projects/${projectId}/permit-workflow`,
    { method: 'GET' },
  );
}

export async function generatePermitWorkflow(projectId: string): Promise<PermitWorkflow> {
  return apiFetch<PermitWorkflow & { success: boolean }>(
    `/sela/projects/${projectId}/permit-workflow/generate`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
  );
}

export async function updatePermitRequirement(
  projectId: string,
  requirementId: string,
  payload: Partial<Pick<PermitRequirement, 'status' | 'owner' | 'dueDate' | 'notes'>>,
): Promise<PermitRequirement> {
  const response = await apiFetch<{ success: boolean; requirement: PermitRequirement }>(
    `/sela/projects/${projectId}/permit-workflow/requirements/${encodeURIComponent(requirementId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return response.requirement;
}

export async function createPermitReviewCycle(
  projectId: string,
  payload: Partial<Pick<PermitReviewCycle, 'status' | 'submittedAt' | 'returnedAt' | 'sourceDocumentIds' | 'notes'>>,
): Promise<PermitReviewCycle> {
  const response = await apiFetch<{ success: boolean; reviewCycle: PermitReviewCycle }>(
    `/sela/projects/${projectId}/permit-workflow/review-cycles`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.reviewCycle;
}

export async function createPermitReviewComment(
  projectId: string,
  cycleId: string,
  payload: Partial<PermitReviewComment> & { commentText: string },
): Promise<PermitReviewComment> {
  const response = await apiFetch<{ success: boolean; comment: PermitReviewComment }>(
    `/sela/projects/${projectId}/permit-workflow/review-cycles/${encodeURIComponent(cycleId)}/comments`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.comment;
}

export async function updatePermitReviewComment(
  projectId: string,
  commentId: string,
  payload: Partial<PermitReviewComment>,
): Promise<PermitReviewComment> {
  const response = await apiFetch<{ success: boolean; comment: PermitReviewComment }>(
    `/sela/projects/${projectId}/permit-workflow/comments/${encodeURIComponent(commentId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return response.comment;
}

export async function createPermitResubmittalBundle(
  projectId: string,
  payload: { changedDocumentIds?: string[] } = {},
): Promise<PermitResubmittalBundle> {
  const response = await apiFetch<{ success: boolean; resubmittalBundle: PermitResubmittalBundle }>(
    `/sela/projects/${projectId}/permit-workflow/resubmittal-bundles`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.resubmittalBundle;
}

// ----------------------------------------------------------------------
// Gate workflow (pre-construction critical path)
// ----------------------------------------------------------------------

export type GateStatus = 'not_started' | 'in_progress' | 'needs_review' | 'complete' | 'blocked' | 'not_applicable' | 'locked' | 'active' | 'ready';
export type GateBlockerStatus = 'open' | 'in_progress' | 'waiting' | 'resolved' | 'waived';
export type GateDeliverableStatus = 'not_started' | 'in_progress' | 'waiting_on_someone' | 'waiting_on_document' | 'blocked' | 'needs_review' | 'complete' | 'skipped' | 'not_applicable' | 'missing';
export type GateDeliverableImportance = 'required' | 'recommended' | 'conditional' | 'optional';
export type GateSeverity = 'low' | 'medium' | 'high' | 'critical';

export type GateSummary = {
  requiredComplete: number;
  requiredUnresolved: number;
  requiredOpen?: number;
  actionsWaitingOnSomeone?: number;
  actionsWaitingOnDocuments?: number;
  actionsNeedsReview?: number;
  skippedWithAcceptedRisk: number;
  blockers: number;
  blockedActions?: number;
  recommendedUnresolved: number;
  recommendation: string;
};

export type RoadmapSummary = {
  currentStage: string;
  currentStageId: string;
  stageGoal: string;
  overallRoadmapStatus: string;
  requiredItemsUnresolved: number;
  blockedItems: number;
  skippedWithAcceptedRisk: number;
  nextGateRecommendation: string;
  dealStage?: string;
};

export type RequirementFollowUp = {
  who?: string;
  what?: string;
  dueDate?: string;
  notes?: string;
};

export type Gate = {
  gateId: string;
  label: string;
  order: number;
  stageId: string;
  description?: string;
  goal?: string;
  status: GateStatus | string;
  targetDate?: string;
  completedAt?: string;
  blockerCount: number;
  criticalBlockerCount: number;
  gateSummary?: GateSummary;
  current?: boolean;
  preview?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type StageProfile = {
  stageId: string;
  label: string;
  gateIds: string[];
  stakeholders: string[];
  deliverables: string[];
  typicalDuration: string;
  costSignal: string;
  failureModes: string[];
};

export type GateDeliverable = {
  deliverableId: string;
  requirementId?: string;
  label: string;
  ownerRole: string;
  gateId: string;
  status: GateDeliverableStatus | string;
  importance?: GateDeliverableImportance | string;
  whyItMatters?: string;
  howToResolve?: string;
  appliesToDevelopmentPaths?: string[];
  suggestedDocuments?: string[];
  suggestedExpert?: string;
  expertQuestions?: string[];
  openQuestions?: string[];
  relatedRiskOrMissingFact?: string;
  timelineExposure?: string;
  softCostExposure?: string;
  carryingCostSensitivity?: string;
  supportsApprovalIds: string[];
  dependencyIds: string[];
  documentIds: string[];
  evidenceNodeIds: string[];
  skipReason?: string;
  skippedAt?: string;
  notApplicableReason?: string;
  completeNote?: string;
  acceptedRisk?: boolean;
  followUp?: RequirementFollowUp;
  userModified?: boolean;
  source?: string;
  sourceBriefMessageId?: string;
  sourceActionIndex?: number;
};

export type GateApproval = {
  approvalId: string;
  label: string;
  issuerRole: string;
  gateId: string;
  status: string;
  requiredDeliverableIds: string[];
};

export type GateDependency = {
  dependencyId: string;
  fromId: string;
  toId: string;
  dependencyType: string;
  lagDays: number;
};

export type GateBlocker = {
  blockerId: string;
  gateId: string;
  title: string;
  description?: string;
  status: GateBlockerStatus | string;
  severity: GateSeverity | string;
  owner?: string;
  dueDate?: string;
  threatenedDate?: string;
  dependencyIds: string[];
  sourceType: string;
  sourceId: string;
  evidenceNodeIds: string[];
  documentIds: string[];
  stakeholderRole?: string;
  deliverableId?: string;
  approvalId?: string;
  notes?: string;
  manual?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type GateTimelineEvent = {
  eventId: string;
  eventType: string;
  label: string;
  date?: string;
  lane: string;
  sourceType: string;
  sourceId: string;
  gateId: string;
  severity?: GateSeverity | string;
};

export type GateWorkflow = {
  workflowStatus?: 'pending_decision_brief' | 'generated' | string;
  statusMessage?: string;
  summary?: RoadmapSummary;
  gates: Gate[];
  stageProfiles: StageProfile[];
  deliverables: GateDeliverable[];
  approvals: GateApproval[];
  blockers: GateBlocker[];
  dependencies: GateDependency[];
  timelineEvents: GateTimelineEvent[];
};

export async function getGateWorkflow(projectId: string): Promise<GateWorkflow> {
  return apiFetch<GateWorkflow & { success: boolean }>(
    `/sela/projects/${projectId}/gate-workflow`,
    { method: 'GET' },
  );
}

export async function recalculateGateWorkflow(projectId: string): Promise<GateWorkflow> {
  return apiFetch<GateWorkflow & { success: boolean }>(
    `/sela/projects/${projectId}/gate-workflow/recalculate`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    },
  );
}

export async function updateGate(
  projectId: string,
  gateId: string,
  payload: Partial<Pick<Gate, 'status' | 'targetDate' | 'completedAt'>>,
): Promise<Gate> {
  const response = await apiFetch<{ success: boolean; gate: Gate }>(
    `/sela/projects/${projectId}/gate-workflow/gates/${encodeURIComponent(gateId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return response.gate;
}

export async function updateGateDeliverable(
  projectId: string,
  deliverableId: string,
  payload: Partial<Pick<GateDeliverable, 'status' | 'skipReason' | 'notApplicableReason' | 'completeNote' | 'followUp'>>,
): Promise<GateDeliverable> {
  const response = await apiFetch<{ success: boolean; deliverable: GateDeliverable }>(
    `/sela/projects/${projectId}/gate-workflow/deliverables/${encodeURIComponent(deliverableId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return response.deliverable;
}

export async function createGateBlocker(
  projectId: string,
  payload: Partial<GateBlocker> & { gateId: string; title: string },
): Promise<GateBlocker> {
  const response = await apiFetch<{ success: boolean; blocker: GateBlocker }>(
    `/sela/projects/${projectId}/gate-workflow/blockers`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.blocker;
}

export async function updateGateBlocker(
  projectId: string,
  blockerId: string,
  payload: Partial<GateBlocker>,
): Promise<GateBlocker> {
  const response = await apiFetch<{ success: boolean; blocker: GateBlocker }>(
    `/sela/projects/${projectId}/gate-workflow/blockers/${encodeURIComponent(blockerId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return response.blocker;
}

// ----------------------------------------------------------------------
// Project people, needed roles, follow-ups, and communication drafts
// ----------------------------------------------------------------------

export type RequirementSourceType = 'gate_deliverable' | 'permit_requirement' | 'gate_blocker' | string;

export type RequirementSourceRef = {
  sourceType: RequirementSourceType;
  sourceId: string;
  label?: string;
  stageId?: string;
  gateId?: string;
  requirementId?: string;
  deliverableId?: string;
};

export type ProjectPersonStatus = 'active' | 'suggested' | 'unassigned' | 'archived';
export type ProjectPersonDerivedStatus =
  | 'active'
  | 'waiting_on_response'
  | 'waiting_on_document'
  | 'overdue_follow_up'
  | 'no_open_asks'
  | 'archived'
  | string;

export type ProjectPerson = {
  projectId?: string;
  personId: string;
  name: string;
  roles: string[];
  company?: string;
  email?: string;
  phone?: string;
  notes?: string;
  relationshipContext?: string;
  status: ProjectPersonStatus | string;
  derivedStatus?: ProjectPersonDerivedStatus;
  linkedRequirementRefs: RequirementSourceRef[];
  openAskCount?: number;
  documentRequestCount?: number;
  lastContactedAt?: string | null;
  nextFollowUpAt?: string | null;
  overdue?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type ProjectRoleNeedStatus = 'suggested' | 'active' | 'assigned' | 'not_needed' | 'archived';

export type ProjectRoleNeed = {
  projectId?: string;
  roleNeedId: string;
  role: string;
  status: ProjectRoleNeedStatus | string;
  whyNeeded?: string;
  suggestedQuestions: string[];
  linkedRequirementRefs: RequirementSourceRef[];
  assignedPersonId?: string;
  derived?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type ProjectFollowUpStatus =
  | 'draft_needed'
  | 'draft_ready'
  | 'sent'
  | 'waiting_on_response'
  | 'waiting_on_document'
  | 'needs_user_review'
  | 'completed'
  | 'closed'
  | 'blocked';

export type CommunicationThreadMetadata = {
  provider?: string;
  threadId?: string;
  messageId?: string;
  draftId?: string;
  subject?: string;
  linkedAt?: string;
};

export type ProjectFollowUp = {
  projectId?: string;
  followUpId: string;
  title: string;
  personId?: string;
  roleNeedId?: string;
  responsibleRole?: string;
  requirementRef?: RequirementSourceRef;
  relatedStage?: string;
  relatedRisk?: string;
  relatedMissingFact?: string;
  openAsk: string;
  questions: string[];
  requestedDocument?: string;
  status: ProjectFollowUpStatus | string;
  dueDate?: string;
  lastContactedAt?: string;
  notes?: string;
  outcome?: string;
  communicationSummary?: string;
  linkedEmail?: CommunicationThreadMetadata;
  latestDraftId?: string;
  latestDraftSubject?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CommunicationDraft = {
  projectId?: string;
  draftId: string;
  followUpId: string;
  personId?: string;
  roleNeedId?: string;
  draftType: string;
  subject: string;
  body: string;
  status: 'ready' | string;
  generatedBy?: 'agent' | 'template' | string;
  generationFallback?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type ProjectOperationsSummary = {
  peopleCount: number;
  openFollowUpCount: number;
  overdueFollowUpCount: number;
  waitingOnDocumentCount: number;
  unassignedRoleNeedCount: number;
};

export type ProjectOperations = {
  people: ProjectPerson[];
  roleNeeds: ProjectRoleNeed[];
  suggestedRoleNeeds: ProjectRoleNeed[];
  followUps: ProjectFollowUp[];
  drafts: CommunicationDraft[];
  summary: ProjectOperationsSummary;
};

export type CreateProjectPersonPayload = Partial<Omit<ProjectPerson, 'personId' | 'createdAt' | 'updatedAt'>> & {
  name?: string;
  roles?: string[];
};

export type CreateProjectRoleNeedPayload = Partial<Omit<ProjectRoleNeed, 'roleNeedId' | 'createdAt' | 'updatedAt'>> & {
  role: string;
};

export type CreateProjectFollowUpPayload = Partial<Omit<ProjectFollowUp, 'followUpId' | 'createdAt' | 'updatedAt'>> & {
  openAsk: string;
};

export type AssignRequirementActionPayload = {
  personId: string;
  requirementRef: RequirementSourceRef;
  openAsk?: string;
  dueDate?: string;
  notes?: string;
  responsibleRole?: string;
};

export async function getProjectOperations(projectId: string): Promise<ProjectOperations> {
  const response = await apiFetch<ProjectOperations & { success: boolean }>(
    `/sela/projects/${projectId}/operations`,
    { method: 'GET' },
  );
  return {
    people: response.people || [],
    roleNeeds: response.roleNeeds || [],
    suggestedRoleNeeds: response.suggestedRoleNeeds || [],
    followUps: response.followUps || [],
    drafts: response.drafts || [],
    summary: response.summary || {
      peopleCount: 0,
      openFollowUpCount: 0,
      overdueFollowUpCount: 0,
      waitingOnDocumentCount: 0,
      unassignedRoleNeedCount: 0,
    },
  };
}

export async function createProjectPerson(
  projectId: string,
  payload: CreateProjectPersonPayload,
): Promise<ProjectPerson> {
  const response = await apiFetch<{ success: boolean; person: ProjectPerson }>(
    `/sela/projects/${projectId}/people`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.person;
}

export async function updateProjectPerson(
  projectId: string,
  personId: string,
  payload: Partial<ProjectPerson>,
): Promise<ProjectPerson> {
  const response = await apiFetch<{ success: boolean; person: ProjectPerson }>(
    `/sela/projects/${projectId}/people/${encodeURIComponent(personId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return response.person;
}

export async function createProjectRoleNeed(
  projectId: string,
  payload: CreateProjectRoleNeedPayload,
): Promise<ProjectRoleNeed> {
  const response = await apiFetch<{ success: boolean; roleNeed: ProjectRoleNeed }>(
    `/sela/projects/${projectId}/role-needs`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.roleNeed;
}

export async function updateProjectRoleNeed(
  projectId: string,
  roleNeedId: string,
  payload: Partial<ProjectRoleNeed>,
): Promise<ProjectRoleNeed> {
  const response = await apiFetch<{ success: boolean; roleNeed: ProjectRoleNeed }>(
    `/sela/projects/${projectId}/role-needs/${encodeURIComponent(roleNeedId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return response.roleNeed;
}

export async function createProjectFollowUp(
  projectId: string,
  payload: CreateProjectFollowUpPayload,
): Promise<ProjectFollowUp> {
  const response = await apiFetch<{ success: boolean; followUp: ProjectFollowUp }>(
    `/sela/projects/${projectId}/follow-ups`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.followUp;
}

export async function assignRequirementAction(
  projectId: string,
  payload: AssignRequirementActionPayload,
): Promise<ProjectFollowUp> {
  const response = await apiFetch<{ success: boolean; followUp: ProjectFollowUp; created?: boolean }>(
    `/sela/projects/${projectId}/operations/assign-requirement`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.followUp;
}

export async function updateProjectFollowUp(
  projectId: string,
  followUpId: string,
  payload: Partial<ProjectFollowUp>,
): Promise<ProjectFollowUp> {
  const response = await apiFetch<{ success: boolean; followUp: ProjectFollowUp }>(
    `/sela/projects/${projectId}/follow-ups/${encodeURIComponent(followUpId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
  return response.followUp;
}

export async function createProjectCommunicationDraft(
  projectId: string,
  followUpId: string,
  payload: { draftType?: string } = {},
): Promise<{ draft: CommunicationDraft; followUp: ProjectFollowUp }> {
  const response = await apiFetch<{
    success: boolean;
    draft: CommunicationDraft;
    followUp: ProjectFollowUp;
  }>(`/sela/projects/${projectId}/follow-ups/${encodeURIComponent(followUpId)}/drafts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return { draft: response.draft, followUp: response.followUp };
}

// ----------------------------------------------------------------------
// Roadmap Action provider search
// ----------------------------------------------------------------------

export type ProviderMatchQuality = 'strong_match' | 'possible_match' | 'weak_match' | 'needs_review';

export type ProviderRoleCategory =
  | 'geotechnical_engineer'
  | 'surveyor'
  | 'arborist'
  | 'civil_engineer'
  | 'architect_designer'
  | 'permit_consultant'
  | 'land_use_attorney'
  | 'general_contractor'
  | 'custom'
  | 'needs_review'
  | string;

export type ProviderSearchCandidate = {
  name: string;
  role: string;
  serviceCategory?: string;
  locationOrServiceArea?: string;
  websiteUrl?: string;
  contactEmail?: string | null;
  phone?: string | null;
  contactPageUrl?: string | null;
  matchQuality: ProviderMatchQuality | string;
  matchRationale?: string;
  evidenceSnippets: string[];
  sourceUrls: string[];
  warnings: string[];
  suggestedNextStep?: string;
};

export type ProviderSearchRecord = {
  projectId: string;
  searchId: string;
  queryHash?: string;
  deliverableId: string;
  gateId?: string;
  stageId?: string;
  requirementId?: string;
  requirementRef?: RequirementSourceRef;
  actionLabel: string;
  actionSummary?: string;
  neededRole: string;
  roleCategory?: ProviderRoleCategory;
  roleSource?: string;
  locationText?: string;
  city?: string;
  state?: string;
  parcelAddress?: string;
  parcelPin?: string;
  developmentPaths?: SelaDevelopmentPath[] | string[];
  dealStage?: SelaDealStage | string;
  priorities?: SelaPriority[] | string[];
  knownRisks?: string[];
  missingFacts?: string[];
  searchQuery?: string;
  searchTerms?: string[];
  searchRadius?: string;
  searchProvider?: 'brave' | string;
  status: 'pending' | 'complete' | 'failed' | string;
  providers: ProviderSearchCandidate[];
  fallbackMessage?: string | null;
  uncertaintyNotes?: string[];
  errorMessage?: string;
  searchedAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateProviderSearchPayload = {
  deliverableId: string;
  requirementRef?: RequirementSourceRef;
  providerRole?: string;
  locationText?: string;
  searchRadius?: string;
  searchTerms?: string[];
  knownRisks?: string[];
  missingFacts?: string[];
};

export async function getProviderSearches(
  projectId: string,
  options: { deliverableId?: string; role?: string } = {},
): Promise<ProviderSearchRecord[]> {
  const params = new URLSearchParams();
  if (options.deliverableId) params.set('deliverableId', options.deliverableId);
  if (options.role) params.set('role', options.role);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await apiFetch<{ success: boolean; providerSearches: ProviderSearchRecord[] }>(
    `/sela/projects/${projectId}/provider-searches${suffix}`,
    { method: 'GET' },
  );
  return response.providerSearches || [];
}

export async function createProviderSearch(
  projectId: string,
  payload: CreateProviderSearchPayload,
): Promise<ProviderSearchRecord> {
  const response = await apiFetch<{ success: boolean; providerSearch: ProviderSearchRecord }>(
    `/sela/projects/${projectId}/provider-searches`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.providerSearch;
}

export async function refreshProviderSearch(
  projectId: string,
  providerSearchId: string,
  payload: CreateProviderSearchPayload,
): Promise<ProviderSearchRecord> {
  const response = await apiFetch<{ success: boolean; providerSearch: ProviderSearchRecord }>(
    `/sela/projects/${projectId}/provider-searches/${encodeURIComponent(providerSearchId)}/refresh`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  return response.providerSearch;
}

// ----------------------------------------------------------------------
// Knowledge graph
// ----------------------------------------------------------------------

/**
 * One source / evidence record on a fact node. Multiple sources
 * accumulate when the same fact is extracted from multiple pages,
 * sheets or PDFs (the backend dedupes facts on canonical
 * (label, value, unit) and appends sources).
 */
export type SelaSourceRef = {
  documentId?: string;
  documentName?: string;
  sheetNumber?: string;
  sheetName?: string;
  pageNumber?: number;
  pageImageKey?: string;
  pageImageUrl?: string; // populated by /evidence/{nodeId} (presigned)
  quote?: string;
  bbox?: number[];
  confidence?: number;
  sourceUrl?: string;
  mapUrl?: string;
};

/**
 * Backwards-compat alias. Older code reads ``evidenceRefs`` /
 * ``SelaEvidenceRef``; the canonical name is ``sources`` /
 * ``SelaSourceRef``. Kept so a half-migrated frontend still compiles.
 */
export type SelaEvidenceRef = SelaSourceRef;

export type SelaGraphNode = {
  nodeId: string;
  nodeType: string;
  label: string;
  value?: string;
  unit?: string;
  confidence?: number;
  /** "zoning" | "site" | "building" | "dimensions" | "parking" | ... */
  category?: string;
  sources?: SelaSourceRef[];

  /** @deprecated use ``sources`` instead. Kept for legacy rows. */
  evidenceRefs?: SelaSourceRef[];
  /** @deprecated only set on legacy rows. */
  documentId?: string;
  /** @deprecated only set on legacy rows. */
  pageNumber?: number;
  /** @deprecated only set on legacy rows. */
  sheetNodeId?: string;

  properties?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
};

export type SelaGraphEdge = {
  sourceNodeId: string;
  targetNodeId: string;
  predicate: string;
  properties?: Record<string, unknown>;
  createdAt?: string;
};

export type SelaGraphPayload = {
  nodes: SelaGraphNode[];
  edges: SelaGraphEdge[];
};

export type SelaFactsPage = {
  facts: SelaGraphNode[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

export async function getSelaProjectGraph(projectId: string): Promise<SelaGraphPayload> {
  const response = await apiFetch<{ success: boolean; nodes: SelaGraphNode[]; edges: SelaGraphEdge[] }>(
    `/sela/projects/${projectId}/graph`,
    { method: 'GET' },
  );
  return { nodes: response.nodes || [], edges: response.edges || [] };
}

export async function listSelaProjectFacts(projectId: string): Promise<SelaGraphNode[]> {
  const response = await apiFetch<{ success: boolean; facts: SelaGraphNode[] }>(
    `/sela/projects/${projectId}/facts`,
    { method: 'GET' },
  );
  return response.facts || [];
}

export async function listSelaProjectFactsPage(
  projectId: string,
  options: { limit: number; offset: number },
): Promise<SelaFactsPage> {
  const params = new URLSearchParams({
    limit: String(options.limit),
    offset: String(options.offset),
  });
  const response = await apiFetch<{ success: boolean } & SelaFactsPage>(
    `/sela/projects/${projectId}/facts?${params.toString()}`,
    { method: 'GET' },
  );
  return {
    facts: response.facts || [],
    total: response.total || 0,
    limit: response.limit || options.limit,
    offset: response.offset || options.offset,
    hasMore: Boolean(response.hasMore),
  };
}

export async function listSelaProjectMissingFacts(projectId: string): Promise<SelaGraphNode[]> {
  const response = await apiFetch<{ success: boolean; missingFacts: SelaGraphNode[] }>(
    `/sela/projects/${projectId}/missing-facts`,
    { method: 'GET' },
  );
  return response.missingFacts || [];
}

export async function getSelaProjectEvidence(
  projectId: string,
  nodeId: string,
): Promise<{ node: SelaGraphNode | null; sources: SelaSourceRef[] }> {
  // The API returns the array under both ``sources`` (canonical) and
  // ``evidence`` (legacy). Accept either so the frontend keeps working
  // before/after the backend deploy.
  const response = await apiFetch<{
    success: boolean;
    node: SelaGraphNode | null;
    sources?: SelaSourceRef[];
    evidence?: SelaSourceRef[];
  }>(`/sela/projects/${projectId}/evidence/${encodeURIComponent(nodeId)}`, {
    method: 'GET',
  });
  return {
    node: response.node,
    sources: response.sources ?? response.evidence ?? [],
  };
}

/**
 * Dev-only: wipe every node and edge under a project. Used during the
 * graph schema restructure to clear out legacy PlanSheet / Document
 * rows. The pipeline repopulates on the next PDF upload.
 */
export async function resetSelaProjectGraph(
  projectId: string,
): Promise<{ deleted: number }> {
  const response = await apiFetch<{ success: boolean; deleted: number }>(
    `/sela/projects/${projectId}/graph`,
    { method: 'DELETE' },
  );
  return { deleted: response.deleted ?? 0 };
}
